import React, { useEffect, useMemo, useState } from "react";
import {
  Box,  Typography, Chip,  TableBody,
  TableRow,
  TableCell,
} from "@mui/material";

import FeatureFilters from "./FeatureFilters";
import { useFeatureRequests } from "./useFeatureRequests";
import ProgressLegend from "./ProgressLegend";
import { ROADMAP_STATUS_OPTIONS, CATEGORY_OPTIONS, STATUS_OPTIONS } from "./featureConfig";
import RoadMapTableHeader from "./RoadMapTableHeader";
import Tooltip from '@mui/material/Tooltip';
import heartLoading from "../../../assests/heartLoading.gif";
import FeatureRequestDialog from "./FeatureRequestDialog";
import MyDatePicker from "./DatePicker";


const MONTH_FORMAT_OPTIONS = { month: "short", year: "2-digit" };
const DEFAULT_ROADMAP_CATEGORIES = ["NEW_FEATURE", "IMPROVEMENT"];

const CATEGORY_MAP = {
  NEW_FEATURE: { label: "New Feature", color: "#2563eb" },
  IMPROVEMENT: { label: "Enhancement", color: "#7c3aed" },
  BUG: { label: "Bug Fix", color: "#dc2626" },
  UI_UX: { label: "UI/UX", color: "#d946ef" },
  PERFORMANCE: { label: "Performance", color: "#ea580c" },
  OTHER: { label: "Other", color: "#6b7280" },
  SECURITY: { label: "Security", color: "#0ea5e9" },
  ONBOARD_PRODUCT: { label: "Onboard Product", color: "#059669" },
};

const findCategoryConfig = (cat) => {
  if (!cat) return CATEGORY_MAP.OTHER;
  const byLabel = Object.values(CATEGORY_MAP).find((c) => c.label.toLowerCase() === String(cat).toLowerCase());
  if (byLabel) return byLabel;
  const key = String(cat).replace(/[\s\-\/]+/g, "_").replace(/__+/g, "_").toUpperCase();
  return CATEGORY_MAP[key] || { label: String(cat), color: CATEGORY_MAP.OTHER.color };
};

const STATUS_PALETTE = {
  "Not Started": { label: "Not Started", bg: "#F1F5F9", color: "#45556C", barColor1: "#CAD5E2", barColor2: "#90A1B9" },
  "In Progress": { label: "In Progress", bg: "#FEF3C6", color: "#BB4D00", barColor1: "#FFB900", barColor2: "#FF8904" },
  "Completed": { label: "Completed", bg: "#D0FAE5", color: "#007A55", barColor1: "#00D492", barColor2: "#00BC7D" },
};


const Roadmap = () => {

  const {  enumsLoading, loadRoadmap, roadmapLoading, isDetailOpen,
    setDetailOpen, selectedRequest, canCurrentUserDelete, commentText,
    setCommentText, handleSubmitComment, isSuperuser, adminForm,
    setAdminForm, handleAdminSave,
    savingAdmin,
    handleDeleteRequest, loadingDetails,
    submittingComment, openRequestDetails } = useFeatureRequests();

  const [filtersVisible, setFiltersVisible] = useState(true);

  const [pendingStatus, setPendingStatus] = useState([]);
  const [pendingCategory, setPendingCategory] = useState(DEFAULT_ROADMAP_CATEGORIES);
  const [appliedStatus, setAppliedStatus] = useState([]);
  const [appliedCategory, setAppliedCategory] = useState(DEFAULT_ROADMAP_CATEGORIES);

  const [startMonth, setStartMonth] = useState("2025-11");
  const [endMonth, setEndMonth] = useState("2026-04");

  const [roadmap, setRoadmap] = useState(null);
  const [loading, setLoading] = useState(true);

  const [page, setPage] = useState(1);
  const page_size = 25;
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [sortBy, setSortBy] = useState(null);
  const [sortOrder, setSortOrder] = useState("asc");
  const [searchBy, setSearchBy] = useState("");
  const [searchValue, setSearchValue] = useState("");
 const [startDate, setStartDate] = useState(() => {
    const today = new Date();
    const start = new Date(today);
    start.setDate(start.getDate() - 30);
    return start.toISOString().split("T")[0];
  }); 
     const [endDate, setEndDate] = useState(() => {
    const today = new Date();
    const end = new Date(today);
    end.setDate(end.getDate() + 90);
    return end.toISOString().split("T")[0];
  });


  const fetchRoadmap = async ({ page: p = 1, category = appliedCategory, status = appliedStatus, start_date = startDate, end_date = endDate, sort_by = sortBy, search_by = searchBy, search_value = searchValue, sort_order = sortOrder } = {}) => {
    setLoading(true);
    try {
      const res = await loadRoadmap({
        start_date,
        end_date,
        category,
        status,
        sort_by,
        sort_order,
        search_by,
        search_value
      });
      if (res) {
        setRoadmap(res);
        const serverPage = typeof res.page === "number" ? res.page : (typeof res.current_page === "number" ? res.current_page : p);
        const serverTotalPages = typeof res.total_pages === "number" ? res.total_pages : (res.total ? Math.max(1, Math.ceil(res.total / page_size)) : 1);
        const serverTotal = typeof res.total === "number" ? res.total : (typeof res.totalFeatures === "number" ? res.totalFeatures : 0);
        setPage(serverPage);
        setTotalPages(serverTotalPages);
        setTotalCount(serverTotal);
      } else {
        setRoadmap(null);
        setTotalPages(1);
        setTotalCount(0);
      }
    } catch (err) {
      setRoadmap(null);
      setTotalPages(1);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const canDeleteSelected = selectedRequest
    ? canCurrentUserDelete(selectedRequest)
    : false;

  useEffect(() => {
    fetchRoadmap({ page: 1 });
  }, []);


  const filteredFeatures = roadmap ? roadmap.features || [] : [];
  

  const normalizeTimelineRange = (timeline) => {
    if (!timeline) return null;
    let start = null;
    let end = null;
    if (Array.isArray(timeline)) {
      timeline.forEach((t) => {
        const s = t?.startDate ? new Date(t.startDate) : null;
        const e = t?.endDate ? new Date(t.endDate) : null;
        if (s && (!start || s < start)) start = s;
        if (e && (!end || e > end)) end = e;
      });
    } else {
      start = timeline.startDate ? new Date(timeline.startDate) : null;
      end = timeline.endDate ? new Date(timeline.endDate) : null;
    }
    if (!start || !end) return null;
    return { start, end };
  };

  const computeBarPercents = (featureRange) => {
    const rangeStart = new Date(startMonth + "-01");
    const rangeEnd = new Date(new Date(endMonth + "-01").getFullYear(), new Date(endMonth + "-01").getMonth() + 1, 0);
    const msPerDay = 24 * 60 * 60 * 1000;
    const totalDays = Math.max(1, Math.round((rangeEnd - rangeStart) / msPerDay) + 1);

    const fStart = featureRange.start;
    const fEnd = featureRange.end;

    const clampedStart = fStart < rangeStart ? rangeStart : fStart;
    const clampedEnd = fEnd > rangeEnd ? rangeEnd : fEnd;
    if (clampedEnd < rangeStart || clampedStart > rangeEnd) return null;

    const offsetDays = Math.round((clampedStart - rangeStart) / msPerDay);
    const widthDays = Math.round((clampedEnd - clampedStart) / msPerDay) + 1;

    const left = (offsetDays / totalDays) * 100;
    const width = (widthDays / totalDays) * 100;
    return { left, width };
  };

  

  const handleApplyFilters = () => {
    setAppliedCategory(pendingCategory);
    setAppliedStatus(pendingStatus);
    setPage(1);
    fetchRoadmap({ page: 1, category: pendingCategory, status: pendingStatus, start_date: startDate, end_date: endDate, });
  };

  const handleResetFilters = () => {
    setPendingStatus([]);
    setPendingCategory(DEFAULT_ROADMAP_CATEGORIES);
    setAppliedStatus([]);
    setAppliedCategory(DEFAULT_ROADMAP_CATEGORIES);
    setPage(1);
    fetchRoadmap({ page: 1, category: DEFAULT_ROADMAP_CATEGORIES, status: [], start_date: startDate, end_date: endDate });
  };


  const handleApplyRange = (selectedStartDate, selectedEndDate) => {
    setStartDate(selectedStartDate);
    setEndDate(selectedEndDate)
    setPage(1);
    fetchRoadmap({ page: 1, category: appliedCategory, status: appliedStatus, start_date: selectedStartDate, end_date: selectedEndDate });
  };

  const handleSortChange = (sort_by, sort_order) => {
    setSortBy(sort_by);
    setSortOrder(sort_order)
    fetchRoadmap({ page: 1, category: pendingCategory, status: pendingStatus, start_date: startDate, end_date: endDate, sort_by: sort_by, sort_order: sort_order, search_by: searchBy, search_value: searchValue })
  }
  const onSearchChange = (key, value) => {
    setSearchBy(key);
    setSearchValue(value);
    fetchRoadmap({ page: 1, category: appliedCategory, status: appliedStatus, start: startMonth, end: endMonth, search_by: key || searchBy, search_value: value || searchValue });
  }
  const formatDateLabel = (dateStr) => {
    if (!dateStr) return "";

    const date = new Date(dateStr);

    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };


  return (
    <Box sx={{ width: "100%", fontFamily: '"Poppins", sans-serif', padding: "16px 16px" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "24px" }}>
            Product Roadmap
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: "14px" }}>
            Timeline view of feature development across categories
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
         
          <MyDatePicker handleApplyRange={handleApplyRange} startDate={startDate} endDate={endDate} setStartDate={setStartDate} setEndDate={setEndDate} />
        </Box>
      </Box>

      <FeatureFilters
        statusFilter={pendingStatus}
        categoryFilter={pendingCategory}
        searchQuery={""}
        total={totalCount}
        onStatusChange={(vals) => setPendingStatus(vals)}
        onCategoryChange={(vals) => setPendingCategory(vals)}
        onSearchChange={() => { }}
        statusOptions={ROADMAP_STATUS_OPTIONS}
        categoryOptions={CATEGORY_OPTIONS}
        filtersVisible={filtersVisible}
        onToggleFilters={() => setFiltersVisible((v) => !v)}
        onResetFilters={handleResetFilters}
        onApplyFilters={handleApplyFilters}
      />

      <ProgressLegend />

      <Box sx={{
        overflowX: "auto", width: "100%",
        minWidth: 980,
        borderCollapse: "collapse",
        background: "#fff",
        border: "1px solid #E5E7EB",
        borderRadius: "10px"
      }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse"
          }}
        >

          <RoadMapTableHeader onSearchChange={onSearchChange} onSortChange={handleSortChange} setStartMonth={setStartMonth} setEndMonth={setEndMonth} startMonth={startMonth} endMonth={endMonth} setSortBy={setSortBy} setSortOrder={setSortOrder} sortOrder={sortOrder} sortBy={sortBy} startDate={startDate} endDate={endDate} />

          <TableBody>
            {loading || roadmapLoading || enumsLoading ? (
              <tr>
                <td colSpan={10} style={{ padding: 24, borderBottom: "1px solid #E6EDF3", textAlign: 'center' }}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    height="50vh"
                    flexDirection="column"
                    gap={2}
                  >
                    <img
                      src={heartLoading}
                      alt="Loading..."
                      style={{ width: "120px", height: "120px" }}
                    />
                    <Typography color="text.secondary" sx={{ fontSize: "18px" }}>
                      Loading feature requests...
                    </Typography>
                  </Box>
                </td>
              </tr>
            ) : filteredFeatures.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} style={{ padding: 24, borderBottom: "1px solid #E6EDF3", textAlign: 'center' }}>
                  No features in this range.
                </TableCell>
              </TableRow>
            ) : (
              filteredFeatures.map((f) => {
                const timelineSource = f.timeline || f.timelines || (f.startDate && f.endDate ? { startDate: f.startDate, endDate: f.endDate } : null);
                const featureRange = normalizeTimelineRange(timelineSource);


                const percents = featureRange ? computeBarPercents(featureRange) : null;

                return (
                  <TableRow onClick={() => openRequestDetails(f)} key={f.id} hover style={{ borderTop: "1px solid #eef2f7", cursor: "pointer" }}>
                    <TableCell style={{ padding: "12px", verticalAlign: "middle" }}>
                      <div style={{ fontWeight: "normal" }}>{f.title}</div>
                    </TableCell>
                    <TableCell style={{ padding: "12px", verticalAlign: "middle" }}>
                      <div style={{ fontSize: 12, color: "#6b7280", marginTop: 6 }}>{f.owner}</div>
                    </TableCell>

                    <TableCell style={{ padding: "12px", verticalAlign: "middle" }}>
                      {(() => {
                        const categoryConfig = findCategoryConfig(f.category);
                        return (
                          <Chip
                            label={categoryConfig.label}
                            size="small"
                            sx={{
                              fontSize: "12px",
                              backgroundColor: `${categoryConfig.color}15`,
                              color: categoryConfig.color,
                              minWidth: 130,
                              justifyContent: "center",
                              borderRadius: 5,
                            }}
                          />
                        );
                      })()}
                    </TableCell>

                    <TableCell
                      style={{
                        padding: "12px 8px",
                        verticalAlign: "middle",
                        width: 720,
                        
                      }}
                    >
                      <div style={{ position: "relative", height: 28, width: "100%" }}>
                        {f.timeline?.startDate && f.timeline?.endDate && percents && (
                          <Tooltip
                            title={`${formatDateLabel(f.timeline.startDate)} → ${formatDateLabel(f.timeline.endDate)}`}
                            arrow
                            placement="top"
                          >
                            <div
                              style={{
                                position: "absolute",
                                left: `${percents.left}%`,
                                width: `${percents.width}%`,
                                height: 20,
                                top: "50%",
                                transform: "translateY(-50%)",
                                borderRadius: 6,
                                boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                                background: `linear-gradient(
                                90deg,
                                ${STATUS_PALETTE[f.status].barColor1},
                                ${STATUS_PALETTE[f.status].barColor2}
                              )`,
                              }}
                            />
                          </Tooltip>
                        )}
                      </div>
                    </TableCell>


                    <TableCell style={{ padding: "12px", verticalAlign: "middle" }}>
                      <Chip
                        label={f.status}
                        size="small"
                        sx={{
                          fontSize: "12px",
                          minWidth: 90,
                          justifyContent: "center",
                          color: STATUS_PALETTE[f.status].color,
                          backgroundColor: `${STATUS_PALETTE[f.status].bg}`,
                        }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </table>
      </Box>

      <FeatureRequestDialog
        open={isDetailOpen}
        onClose={() => setDetailOpen(false)}
        request={selectedRequest}
        canDeleteRequest={canDeleteSelected}
        commentValue={commentText}
        onCommentChange={setCommentText}
        onSubmitComment={handleSubmitComment}
        isSuperuser={isSuperuser}
        adminForm={adminForm}
        onAdminFormChange={(updates) =>
          setAdminForm((prev) => ({ ...prev, ...updates }))
        }
        onAdminSave={handleAdminSave}
        onDelete={handleDeleteRequest}
        saving={savingAdmin}
        statusOptions={STATUS_OPTIONS}
        loadingDetails={loadingDetails}
        submittingComment={submittingComment}
      />
    </Box>
  );
};

export default Roadmap;